import { readAuthData, readUserData } from '@/services/authStorage'

export interface AuthData {
  id: number
  isAuthenticated: boolean
  tokenExpiry: number // milliseconds since epoch (Date.getTime())
  email: string
  name: string
  profilePicture: string
  idToken: string
  description: string
  role: string
  loginTime?: number // milliseconds since epoch — when session was created
}

export interface UserData {
  id: number
  role: string
  user_modules: string // "ModA,,,ModB" format
  description: string
  [key: string]: unknown
}

export interface CurrentUserReturn {
  readonly authData: AuthData | null
  readonly user: UserData | null
  readonly userId: number | null
  readonly isLoggedIn: boolean
  readonly userName: string
  readonly userRole: string // from "user" key (authoritative)
  readonly authToken: string
  readonly profilePicture: string
  readonly userModules: string[] // parsed from ",,," separator
  readonly isAdmin: boolean
  readonly isSuperAdmin: boolean
  readonly isSam: boolean
  readonly isAuditor: boolean
}

// Values are cached once per useCurrentUser() call — auth changes only at login/logout
// boundaries (full page navigation), so within-call staleness is not a concern.
// sessionStorage `storage` events only fire in other tabs, not the same tab.
export function useCurrentUser(): CurrentUserReturn {
  const auth = readAuthData()
  const user = readUserData()
  return {
    get authData() {
      return auth
    },
    get user() {
      return user
    },
    get userId() {
      return auth?.id ?? null
    },
    get isLoggedIn() {
      if (!auth?.isAuthenticated) return false
      return Date.now() < auth.tokenExpiry
    },
    get userName() {
      return auth?.name ?? ''
    },
    get userRole() {
      return user?.role ?? ''
    },
    get authToken() {
      return auth?.idToken ?? ''
    },
    get profilePicture() {
      return auth?.profilePicture ?? ''
    },
    get userModules() {
      const modules = user?.user_modules ?? ''
      if (!modules) return []
      return modules
        .split(',,,')
        .map((m) => m.trim())
        .filter((m) => m.length > 0)
    },
    get isAdmin() {
      const r = user?.role ?? ''
      return r === 'super_admin' || r === 'admin'
    },
    get isSuperAdmin() {
      return (user?.role ?? '') === 'super_admin'
    },
    get isSam() {
      return (user?.role ?? '') === 'sam'
    },
    get isAuditor() {
      return (user?.role ?? '') === 'auditor'
    },
  }
}
